import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { ArrowLeft, Package, Check, AlertTriangle, Building2 } from "lucide-react";
import { AttachmentPicker } from "@/components/AttachmentPicker";
import { fileSrc } from "@/lib/uploads";
import { useAuth } from "@/contexts/AuthContext";
import { readDraft, writeDraft, clearDraft } from "@/lib/drafts";

export default function VigiladorMaterials() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [siteDetected, setSiteDetected] = useState(null); // {site_id, site_name, source} | null
  const [siteLoading, setSiteLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [direction, setDirection] = useState("recepcion");
  const [notes, setNotes] = useState("");
  const [evidence, setEvidence] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyFilterSite, setHistoryFilterSite] = useState("");
  const [busy, setBusy] = useState(false);
  // Fix BO (2026-11-03): borrador en el celular (ver lib/drafts.js). draftRef: undefined = sin leer,
  // objeto = borrador pendiente de aplicar cuando cargan los elementos, null = nada pendiente.
  const { user } = useAuth();
  const draftKey = user ? `checklist:${user.id}` : null;
  const draftRef = useRef(undefined);
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    if (!draftKey || draftRef.current !== undefined) return;
    const d = readDraft(draftKey);
    draftRef.current = d && d.clientId ? d : null;
    if (draftRef.current) setClientId(d.clientId);
  }, [draftKey]);
  useEffect(() => {
    if (!draftKey || draftRef.current !== null) return; // sin leer todavía, o borrador pendiente de aplicar
    const meaningful = notes.trim() || evidence.length || Object.values(statuses).some((s) => s === "faltante");
    if (clientId && meaningful) writeDraft(draftKey, { clientId, siteId, direction, statuses, notes, evidence });
    else clearDraft(draftKey);
  }, [draftKey, clientId, siteId, direction, statuses, notes, evidence]);
  const resetChecklist = () => {
    const init = {}; items.forEach((it) => { init[it.id] = "recibido"; });
    setStatuses(init); setEvidence([]); setNotes("");
  };
  const discardDraft = () => { clearDraft(draftKey); resetChecklist(); setRestored(false); };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/clients"); setClients(data); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  // Al elegir cliente: buscamos sus sedes + intentamos deducir en cuál está
  // el vigilador (ronda activa de hoy o turno del Cronograma). Si no se
  // puede deducir y hay más de una sede, se le pide elegir antes de mostrar
  // el checklist. Sin sedes cargadas, sigue funcionando igual que siempre.
  useEffect(() => {
    setSites([]); setSiteId(""); setSiteDetected(null); setItems([]); setHistory([]);
    if (!clientId) return;
    (async () => {
      setSiteLoading(true);
      try {
        const [s, d] = await Promise.all([
          api.get(`/clients/${clientId}/sites`),
          api.get(`/clients/${clientId}/materials/my-site`),
        ]);
        setSites(s.data || []);
        if (d.data?.site_id) { setSiteDetected(d.data); setSiteId(d.data.site_id); }
        else if (draftRef.current?.clientId === clientId && (s.data || []).some((x) => x.id === draftRef.current.siteId)) {
          setSiteId(draftRef.current.siteId); // Fix BO: la sede elegida a mano antes de perder la página
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setSiteLoading(false); }
    })();
  }, [clientId]);

  // Fix BO (2026-11-03): antes no miraba siteId, así que al elegir la sede a mano el checklist no aparecía nunca.
  const showSiteChoice = clientId && !siteLoading && sites.length > 0 && !siteDetected;
  const needsSiteChoice = showSiteChoice && !siteId;

  useEffect(() => {
    if (!clientId || needsSiteChoice) return;
    (async () => {
      try {
        const [m, h] = await Promise.all([api.get(`/clients/${clientId}/materials`), api.get(`/material-handoffs?client_id=${clientId}`)]);
        const effective = m.data.filter((it) => {
          if (it.site_id) return it.site_id === siteId;
          if (it.scope === "central_only") return !siteId;
          return true;
        });
        setItems(effective); setHistory(h.data);
        const init = {}; effective.forEach((it) => { init[it.id] = "recibido"; });
        const dr = draftRef.current;
        if (dr && dr.clientId === clientId) {
          effective.forEach((it) => { if (dr.statuses && dr.statuses[it.id]) init[it.id] = dr.statuses[it.id]; });
          setDirection(dr.direction || "recepcion"); setNotes(dr.notes || ""); setEvidence(dr.evidence || []);
          draftRef.current = null; setRestored(true);
        }
        setStatuses(init);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [clientId, siteId, needsSiteChoice]);

  const submit = async () => {
    if (!clientId || items.length === 0) return;
    // Fix BO (2026-11-03): la foto es opcional (pedido del cliente); se puede adjuntar una.
    setBusy(true);
    try {
      const payload = {
        client_id: clientId,
        site_id: siteId || null,
        direction,
        items: items.map((it) => ({ material_id: it.id, name: it.name, status: statuses[it.id] || "recibido" })),
        photo_url: evidence[0]?.url || null,
        notes: notes || null,
      };
      const { data } = await api.post("/material-handoffs", payload);
      toast.success(data.has_missing ? "Registrado con faltantes ⚠" : "Checklist confirmado ✓");
      clearDraft(draftKey); resetChecklist(); setRestored(false);
      const h = await api.get(`/material-handoffs?client_id=${clientId}`);
      setHistory(h.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const filteredHistory = historyFilterSite
    ? history.filter((h) => (historyFilterSite === "general" ? !h.site_id : h.site_id === historyFilterSite))
    : history;

  return (
    <div style={{ padding: "8px 16px" }}>
      <Link to=".." style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 18px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Check list</h1>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div className="field"><label>Cliente *</label>
          <select value={clientId} onChange={(e) => { draftRef.current = null; setRestored(false); setClientId(e.target.value); }} data-testid="mat-client">
            <option value="">Elegir cliente...</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {clientId && siteLoading && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Buscando tu sede...</div>}

        {clientId && !siteLoading && siteDetected && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#16a34a", fontWeight: 700, margin: "4px 0 8px" }} data-testid="mat-site-detected">
            <Building2 size={13}/> Sede detectada automáticamente: {siteDetected.site_name} ({siteDetected.source === "ronda" ? "según tu ronda de hoy" : "según tu turno de hoy"})
          </div>
        )}

        {restored && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 12px", margin: "4px 0 10px", borderLeft: "4px solid #16a34a", background: "var(--invict-bone-2)" }} data-testid="mat-draft-restored">
            <span style={{ fontSize: 12, fontWeight: 700 }}>Recuperamos el checklist que estabas cargando.</span>
            <button type="button" className="btn-ghost" onClick={discardDraft} style={{ padding: "6px 10px", fontSize: 11 }} data-testid="mat-draft-discard">Descartar</button>
          </div>
        )}

        {showSiteChoice && (
          <div className="field">
            <label>Sede *</label>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="mat-site-select">
              <option value="">Elegir sede...</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "4px 0 0" }}>No pudimos deducir tu sede automáticamente. Elegila para ver su checklist.</p>
          </div>
        )}

        {clientId && !siteLoading && !needsSiteChoice && (
          <>
            <div className="field"><label>Tipo de movimiento</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { v: "recepcion", label: "Recepción (entro al turno)" },
                  { v: "entrega", label: "Entrega (salgo del turno)" },
                ].map(({ v, label }) => (
                  <button key={v} type="button" onClick={() => setDirection(v)}
                    style={{ flex: 1, padding: 10, border: `2px solid ${direction === v ? "#BF3437" : "var(--invict-bone-3)"}`, background: direction === v ? "#BF3437" : "white", color: direction === v ? "white" : "var(--invict-text-muted)", fontWeight: 800, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", borderRadius: 2, cursor: "pointer" }}
                    data-testid={`mat-direction-${v}`}
                  >{label}</button>
                ))}
              </div>
            </div>

            {items.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Este cliente no tiene elementos de trabajo configurados. Pedile a un administrador que los cargue.</div>
            ) : (
              <>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 8, marginTop: 10 }}>Checklist</div>
                {items.map((it) => (
                  <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--invict-bone-3)" }}>
                    <Package size={16} style={{ color: "#0A0A0A" }} />
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>{it.name}{it.site_id && <span style={{ marginLeft: 6, fontSize: 10, color: "var(--invict-text-muted)", fontWeight: 700 }}>({it.site_name})</span>}</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[
                        { v: "recibido", label: "OK", color: "#16a34a", icon: Check },
                        { v: "faltante", label: "Falta", color: "#BF3437", icon: AlertTriangle },
                      ].map(({ v, label, color, icon: Ic }) => (
                        <button key={v} onClick={() => setStatuses({ ...statuses, [it.id]: v })}
                          style={{ padding: "6px 10px", border: `2px solid ${statuses[it.id] === v ? color : "var(--invict-bone-3)"}`, background: statuses[it.id] === v ? color : "white", color: statuses[it.id] === v ? "white" : "var(--invict-text-muted)", fontWeight: 700, fontSize: 11, borderRadius: 2, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
                          data-testid={`mat-${it.id}-${v}`}
                        ><Ic size={11}/> {label}</button>
                      ))}
                    </div>
                  </div>
                ))}
                <button onClick={submit} disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12, marginTop: 14 }} data-testid="mat-submit">{busy ? "Guardando..." : "Confirmar checklist"}</button>
              </>
            )}

            <div className="field" style={{ marginTop: 14 }}><label>Foto evidencia (opcional)</label>
              <AttachmentPicker value={evidence} onChange={setEvidence} accept="image" max={1} label="Adjuntar foto"/>
            </div>
            <div className="field"><label>Observaciones</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ej. radio sin batería, llaves entregadas..." data-testid="mat-notes"/>
            </div>
          </>
        )}
      </div>

      {clientId && history.length > 0 && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A" }}>Historial reciente</div>
            {sites.length > 0 && (
              <select value={historyFilterSite} onChange={(e) => setHistoryFilterSite(e.target.value)} data-testid="mat-history-site-filter" style={{ fontSize: 11 }}>
                <option value="">Todas las sedes</option>
                <option value="general">Sin sede (general)</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            {filteredHistory.slice(0, 10).map((h) => (
              <div key={h.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 10 }} data-testid={`mat-history-${h.id}`}>
                {h.photo_url && <img src={fileSrc(h.photo_url)} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}/>}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 800, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h.direction}</span> · {h.user_name}{h.user_deleted && <DeletedUserBadge id={h.id} />}
                      <span style={{ color: "var(--invict-text-muted)" }}> · {h.site_name || "General"}</span>
                    </div>
                    {h.has_missing && <span className="badge red"><AlertTriangle size={9} style={{ marginRight: 3 }}/>FALTANTE</span>}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--invict-text-muted)" }}>{new Date(h.created_at).toLocaleString("es-AR")}</div>
                  {h.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic", marginTop: 2 }}>{h.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
